import { pubsub } from '@services';
import { debugError } from '@utils';

class PricingTable extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;
    this.#init();
  }

  disconnectedCallback() {
    this.unsubscribeVariantChange?.();
    this.unsubscribeChannelChange?.();
  }

  #init() {
    this.#initTabFiltering();
    this.#applyRowStyling();

    this.unsubscribeVariantChange = pubsub.subscribe(
      `variant:changed:${this.productId}`,
      ({ variant }) => {
        this.querySelectorAll('.variant-row').forEach((row) => {
          row.classList.toggle('selected', row.dataset.variantId === variant.id.toString());
        });
      }
    );

    this.unsubscribeChannelChange = pubsub.subscribe(
      'channel:changed',
      this.#updateChannelPrices.bind(this)
    );
  }

  async #updateChannelPrices() {
    const rows = this.querySelectorAll('.variant-row');

    for (const row of rows) {
      try {
        const response = await fetch(
          `${window.location.pathname}?variant=${row.dataset.variantId}&section_name=pricing-table`
        );
        if (!response.ok) continue;

        const html = await response.text();
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;

        const responseRow = tempDiv.querySelector(`[data-variant-id="${row.dataset.variantId}"]`);
        const newPrice = responseRow?.querySelector('.offer-price');
        const currentPrice = row.querySelector('.offer-price');

        if (newPrice && currentPrice) {
          currentPrice.innerHTML = newPrice.innerHTML;
        }
      } catch (error) {
        debugError('Price update failed:', row.dataset.variantId, error);
      }
    }
  }

  #initTabFiltering() {
    const tabs = this.querySelectorAll('.pricing-tab');
    if (tabs.length === 0) return;

    // If no "all" tab exists, select the first tab by default
    const allTab = Array.from(tabs).find((tab) => tab.dataset.group === 'all');
    if (!allTab && tabs.length > 0) {
      const firstTab = tabs[0];
      firstTab.setAttribute('aria-selected', 'true');

      // Scroll first tab into view on mobile
      this.#scrollTabIntoView(firstTab);

      // Filter rows based on first tab's group
      const selectedGroup = firstTab.dataset.group;
      this.querySelectorAll('.pricing-row').forEach((row) => {
        const groups = (row.dataset.group || '').split(',').map((g) => g.trim());
        row.style.display = groups.includes(selectedGroup) ? '' : 'none';
      });
      this.#applyRowStyling();
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const selectedGroup = tab.dataset.group;

        tabs.forEach((t) => t.setAttribute('aria-selected', 'false'));
        tab.setAttribute('aria-selected', 'true');

        // Scroll active tab into view on mobile
        this.#scrollTabIntoView(tab);

        this.querySelectorAll('.pricing-row').forEach((row) => {
          if (selectedGroup === 'all') {
            row.style.display = '';
          } else {
            const groups = (row.dataset.group || '').split(',').map((g) => g.trim());
            row.style.display = groups.includes(selectedGroup) ? '' : 'none';
          }
        });

        this.#applyRowStyling();
      });
    });
  }

  #scrollTabIntoView(activeTab) {
    const tabContainer = this.querySelector('.pricing-table-tabs .flex');
    if (!tabContainer) return;

    const containerRect = tabContainer.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();

    const containerCenter = containerRect.left + containerRect.width / 2;
    const tabCenter = tabRect.left + tabRect.width / 2;
    const scrollOffset = tabCenter - containerCenter;

    tabContainer.scrollBy({
      left: scrollOffset,
      behavior: 'smooth',
    });
  }

  #applyRowStyling() {
    const visibleRows = this.querySelectorAll('.pricing-row[style=""], .pricing-row:not([style])');

    visibleRows.forEach((row, index) => {
      const offerCell = row.querySelector('.offer-cell');
      const isEven = index % 2 === 0;
      const isLast = index === visibleRows.length - 1;

      row.classList.remove('bg-white', 'bg-gray-50');
      if (!offerCell) return;

      offerCell.classList.remove('bg-gray-200', 'bg-gray-300', 'rounded-b-xl');
      row.classList.add(isEven ? 'bg-white' : 'bg-gray-50');
      offerCell.classList.add(isEven ? 'bg-gray-300' : 'bg-gray-200');

      if (isLast) offerCell.classList.add('rounded-b-xl');
    });
  }
}

customElements.define('pricing-table-element', PricingTable);
